import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { ArrowLeft, Users, Car, Briefcase, MessageSquare, TrendingUp, Edit, Phone, Mail, MapPin, Calendar, Save, Plus, Trash2, AlertTriangle, Search, RefreshCw, Zap, Clock, MoreHorizontal, User, CheckCircle, Eye, Printer, Edit2, ExternalLink, FileText } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { toast } from 'sonner';
import ContactsManagement from './ContactsManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDetailView = ({ getAuthHeaders }) => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [counties, setCounties] = useState([]);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    address_3: '',
    city: '',
    county: '',
    postcode: '',
    customer_type: 'individual',
    tags: [],
    source: '',
    status: 'active',
    notes: '',
    email_opt_in: true,
    sms_opt_in: true,
    whatsapp_opt_in: false
  });

  // Vehicle management state
  const [vehicles, setVehicles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [isVehicleDialogOpen, setIsVehicleDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isDvlaLookupLoading, setIsDvlaLookupLoading] = useState(false);
  
  // Quick Quote state
  const [isQuickQuoteOpen, setIsQuickQuoteOpen] = useState(false);
  const [quickQuoteContact, setQuickQuoteContact] = useState(null);
  const [services, setServices] = useState([]);
  const [isQuickScheduleOpen, setIsQuickScheduleOpen] = useState(false);
  const [pendingQuoteId, setPendingQuoteId] = useState(null);
  const [scheduleData, setScheduleData] = useState({
    scheduled_start: '',
    scheduled_end: '',
    manual_duration: '',
    scheduled_location: '',
    address_1: '',
    address_2: '',
    address_3: '',
    city: '',
    county: '',
    postcode: '',
    location_type: 'mobile',
    flexible_timing: false,
    special_instructions: ''
  });
  const [quickQuoteData, setQuickQuoteData] = useState({
    contact_id: null,
    vehicle_ids: [],
    items: [],
    notes_public: '',
    notes_internal: '',
    valid_until: ''
  });
  const [addVehicleForItemIndex, setAddVehicleForItemIndex] = useState(null);
  const [vehicleFormData, setVehicleFormData] = useState({
    contact_id: '__no_contact__',
    registration_number: '',
    make: '',
    model: '',
    year: '',
    color: '',
    vehicle_type: '',
    body_type: '__not_specified__',
    fuel_type: '__not_specified__',
    transmission: '__not_specified__',
    engine_size: '',
    mileage: '',
    vin_number: '',
    insurance_company: '',
    insurance_renewal_date: '',
    mot_expiry_date: '',
    mot_status: '',
    tax_status: '',
    co2_emissions: '',
    special_requirements: '',
    notes: ''
  });
  const [tagInput, setTagInput] = useState('');

  // Communication Log state
  const [communicationLogs, setCommunicationLogs] = useState([]);
  const [isCommLogDialogOpen, setIsCommLogDialogOpen] = useState(false);
  const [editingCommLog, setEditingCommLog] = useState(null);
  const [commLogFormData, setCommLogFormData] = useState({
    contact_name: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    next_callback_date: '',
    communication_method: '',
    contact_made: false,
    company_name: '',
    company_phone: '',
    contact_phone: '',
    contact_email: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [editingNotesValue, setEditingNotesValue] = useState('');
  const [editingDateId, setEditingDateId] = useState(null);
  const [editingDateValue, setEditingDateValue] = useState('');
  const [editingCallbackId, setEditingCallbackId] = useState(null);
  const [editingCallbackValue, setEditingCallbackValue] = useState('');
  const [editingMethodId, setEditingMethodId] = useState(null);
  const [editingMethodValue, setEditingMethodValue] = useState('');
  const [editingContactNameId, setEditingContactNameId] = useState(null);
  const [editingContactNameValue, setEditingContactNameValue] = useState('');
  const [isCustomContactNameInline, setIsCustomContactNameInline] = useState(false);


  const [isCustomContactName, setIsCustomContactName] = useState(false);



  const notesInputRef = useRef(null);



  useEffect(() => {
    if (customerId) {
      fetchCustomerDetails();
      fetchCounties();
      fetchVehicles();
      fetchContacts();
      fetchQuotesAndJobs();
      fetchServices();
      fetchCommunicationLogs();
    }
  }, [customerId]);

  // Handle vehicle URL parameter to auto-open vehicle dialog
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const vehicleId = params.get('vehicle');
    
    if (vehicleId && vehicles.length > 0) {
      // Find the vehicle and open the edit dialog
      const vehicle = vehicles.find(v => v.id === vehicleId);
      if (vehicle) {
        openVehicleDialog(vehicle);
      }
    }
  }, [location.search, vehicles]);


  const fetchCounties = async () => {
    try {
      const response = await axios.get(`${API}/counties`, getAuthHeaders());
      setCounties(response.data);
    } catch (error) {
      console.error('Failed to fetch counties:', error);
    }
  };

  const fetchCustomerDetails = async () => {
    try {
      const [customerRes, insightsRes] = await Promise.all([
        axios.get(`${API}/customers/${customerId}`, getAuthHeaders()),
        axios.get(`${API}/customers/${customerId}/insights`, getAuthHeaders())
      ]);
      
      setCustomer(customerRes.data);
      setInsights(insightsRes.data);
    } catch (error) {
      toast.error('Failed to load customer details');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Vehicle management functions
  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}/vehicles`, getAuthHeaders());
      setVehicles(response.data);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      toast.error('Failed to load vehicles');
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts?customer_id=${customerId}`, getAuthHeaders());
      console.log('Fetched contacts:', response.data); // Debug log
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchQuotesAndJobs = async () => {
    try {
      // Fetch quotes for this customer
      const quotesResponse = await axios.get(`${API}/quotes?customer_id=${customerId}`, getAuthHeaders());
      setQuotes(quotesResponse.data);
      
      // Fetch jobs for this customer
      const jobsResponse = await axios.get(`${API}/jobs?customer_id=${customerId}`, getAuthHeaders());
      setJobs(jobsResponse.data);
    } catch (error) {
      console.error('Failed to fetch quotes and jobs:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`, getAuthHeaders());
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchCommunicationLogs = async () => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}/communication_logs`, getAuthHeaders());
      setCommunicationLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch communication logs:', error);
      toast.error('Failed to load communication logs');
    }
  };

  // Get vehicle type icon emoji
  const getVehicleTypeIcon = (vehicleType) => {
    switch (vehicleType) {
      case 'van':
      case 'truck':
        return '🚐';
      case 'motorcycle':
        return '🏍️';
      case 'motorhome':
        return '🚌';
      case 'suv':
        return '🚙';
      case 'convertible':
        return '🏎️';
      default:
        return '🚗';
    }
  };


  // Quick Quote handlers
  const openQuickQuote = (contact = null) => {
    setQuickQuoteContact(contact);
    setQuickQuoteData({
      contact_id: contact?.id || null,
      vehicle_ids: [],
      items: [{
        service_id: null,
        description: '',
        sku: '',
        quantity: 1,
        unit_price: 0,
        cost_price: 0,
        duration: null,
        discount_value: 0,
        tax_rate: 20.0
      }],
      notes_public: '',
      notes_internal: '',
      valid_until: ''
    });
    setIsQuickQuoteOpen(true);
  };

  // Auto-open Quick Quote if navigated from customer list
  useEffect(() => {
    if (location.state?.openQuickQuote && customer) {
      openQuickQuote();
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, customer]);

  const addQuickQuoteItem = () => {
    // Auto-populate vehicle if customer has exactly one vehicle
    const autoVehicleId = vehicles.length === 1 ? vehicles[0].id : null;
    
    setQuickQuoteData({
      ...quickQuoteData,
      items: [...quickQuoteData.items, {
        service_id: null,
        vehicle_id: autoVehicleId,
        description: '',
        sku: '',
        quantity: 1,
        unit_price: 0,
        cost_price: 0,
        duration: null,
        discount_value: 0,
        tax_rate: 20.0
      }]
    });
  };

  const removeQuickQuoteItem = (index) => {
    const newItems = quickQuoteData.items.filter((_, i) => i !== index);
    setQuickQuoteData({ ...quickQuoteData, items: newItems });
  };

  const updateQuickQuoteItem = (index, field, value) => {
    const newItems = [...quickQuoteData.items];
    newItems[index][field] = value;
    
    // If service selected, auto-fill fields
    if (field === 'service_id' && value) {
      const service = services.find(s => s.id === value);
      if (service) {
        newItems[index].description = service.description || service.name;
        newItems[index].unit_price = service.default_price || 0;
        newItems[index].cost_price = service.cost || 0;
        newItems[index].duration = service.default_duration_minutes || null;
      }
    }
    
    setQuickQuoteData({ ...quickQuoteData, items: newItems });
  };


  const calculateQuoteTotal = () => {
    let subtotal = 0;
    let taxTotal = 0;
    
    quickQuoteData.items.forEach(item => {
      const lineSubtotal = (item.quantity * item.unit_price) - item.discount_value;
      const lineTax = lineSubtotal * (item.tax_rate / 100);
      subtotal += lineSubtotal;
      taxTotal += lineTax;
    });
    
    return { subtotal, taxTotal, total: subtotal + taxTotal };
  };

  const handleSaveAndSendQuote = async () => {
    try {
      // Validate
      if (customer.customer_type === 'business' && !quickQuoteData.contact_id) {
        toast.error('Please select a contact for business customers');
        return;
      }
      if (quickQuoteData.items.length === 0 || !quickQuoteData.items[0].description) {
        toast.error('Please add at least one item to the quote');
        return;
      }

      const totals = calculateQuoteTotal();
      
      // Create quote
      const quotePayload = {
        customer_id: customerId,
        contact_id: quickQuoteData.contact_id,
        vehicle_ids: quickQuoteData.vehicle_ids,
        items: quickQuoteData.items,
        subtotal: totals.subtotal,
        tax_total: totals.taxTotal,
        total: totals.total,
        status: 'sent',
        valid_until: quickQuoteData.valid_until || null,
        notes_public: quickQuoteData.notes_public,
        notes_internal: quickQuoteData.notes_internal,
        currency: 'GBP'
      };

      const response = await axios.post(`${API}/quotes`, quotePayload, getAuthHeaders());
      toast.success('Quote created and marked as sent!');
      setIsQuickQuoteOpen(false);
      fetchQuotesAndJobs();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create quote';
      toast.error(errorMessage);
    }
  };

  const handleBookNow = async () => {
    try {
      // Validate
      if (customer.customer_type === 'business' && !quickQuoteData.contact_id) {
        toast.error('Please select a contact for business customers');
        return;
      }
      if (quickQuoteData.items.length === 0 || !quickQuoteData.items[0].description) {
        toast.error('Please add at least one item');
        return;
      }

      const totals = calculateQuoteTotal();
      
      // Calculate total duration for auto-fill
      const totalDuration = quickQuoteData.items.reduce((sum, item) => {
        return sum + ((item.duration || 0) * item.quantity);
      }, 0);
      
      // Step 1: Create quote as accepted
      const quotePayload = {
        customer_id: customerId,
        contact_id: quickQuoteData.contact_id,
        vehicle_ids: quickQuoteData.vehicle_ids,
        items: quickQuoteData.items,
        subtotal: totals.subtotal,
        tax_total: totals.taxTotal,
        total: totals.total,
        status: 'accepted',
        valid_until: quickQuoteData.valid_until || null,
        notes_public: quickQuoteData.notes_public,
        notes_internal: quickQuoteData.notes_internal,
        currency: 'GBP'
      };

      const quoteResponse = await axios.post(`${API}/quotes`, quotePayload, getAuthHeaders());
      const quoteId = quoteResponse.data.id;
      
      toast.success('Quote created and accepted!');
      
      // Step 2: Close Quick Quote and open scheduling dialog
      setIsQuickQuoteOpen(false);
      setPendingQuoteId(quoteId);
      
      // Auto-populate customer address for mobile jobs
      setScheduleData({
        scheduled_start: '',
        scheduled_end: '',
        manual_duration: totalDuration > 0 ? totalDuration : '',
        scheduled_location: '',
        address_1: customer.address_1 || '',
        address_2: customer.address_2 || '',
        address_3: customer.address_3 || '',
        city: customer.city || '',
        county: customer.county || '',
        postcode: customer.postcode || '',
        location_type: 'mobile',
        flexible_timing: false,
        special_instructions: ''
      });
      setIsQuickScheduleOpen(true);
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create booking';
      toast.error(errorMessage);
    }
  };

  const handleCompleteScheduling = async () => {
    if (!scheduleData.scheduled_start) {
      toast.error('Please select a start date and time');
      return;
    }

    try {
      const params = new URLSearchParams();
      if (scheduleData.scheduled_start) params.append('scheduled_start', scheduleData.scheduled_start);
      if (scheduleData.scheduled_end) params.append('scheduled_end', scheduleData.scheduled_end);
      if (scheduleData.scheduled_location) params.append('scheduled_location', scheduleData.scheduled_location);
      // Structured address fields
      if (scheduleData.address_1) params.append('address_1', scheduleData.address_1);
      if (scheduleData.address_2) params.append('address_2', scheduleData.address_2);
      if (scheduleData.address_3) params.append('address_3', scheduleData.address_3);
      if (scheduleData.city) params.append('city', scheduleData.city);
      if (scheduleData.county) params.append('county', scheduleData.county);
      if (scheduleData.postcode) params.append('postcode', scheduleData.postcode);
      if (scheduleData.location_type) params.append('location_type', scheduleData.location_type);
      if (scheduleData.flexible_timing) params.append('flexible_timing', scheduleData.flexible_timing);
      if (scheduleData.special_instructions) params.append('special_instructions', scheduleData.special_instructions);
      
      await axios.post(
        `${API}/quotes/${pendingQuoteId}/convert-to-jobs?${params.toString()}`, 
        {}, 
        getAuthHeaders()
      );
      
      toast.success('Job scheduled successfully!');
      setIsQuickScheduleOpen(false);
      setPendingQuoteId(null);
      fetchQuotesAndJobs();
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to schedule job';
      toast.error(errorMessage);
    }
  };

  const calculateAndSetEndTime = (startDateTime, durationMinutes) => {
    if (!startDateTime || !durationMinutes) return;
    
    const startDate = new Date(startDateTime);
    const endDate = new Date(startDate.getTime() + parseInt(durationMinutes) * 60000);
    
    const year = endDate.getFullYear();
    const month = String(endDate.getMonth() + 1).padStart(2, '0');
    const day = String(endDate.getDate()).padStart(2, '0');
    const hours = String(endDate.getHours()).padStart(2, '0');
    const minutes = String(endDate.getMinutes()).padStart(2, '0');
    const endDateString = `${year}-${month}-${day}T${hours}:${minutes}`;
    
    setScheduleData(prev => ({
      ...prev,
      scheduled_end: endDateString
    }));
  };

  const handleStartTimeChange = (startDateTime) => {
    setScheduleData(prev => ({
      ...prev,
      scheduled_start: startDateTime
    }));
    
    if (startDateTime && scheduleData.manual_duration) {
      calculateAndSetEndTime(startDateTime, scheduleData.manual_duration);
    }
  };

  const handleDurationChange = (duration) => {
    setScheduleData(prev => ({
      ...prev,
      manual_duration: duration
    }));
    
    if (scheduleData.scheduled_start && duration) {
      calculateAndSetEndTime(scheduleData.scheduled_start, duration);
    }
  };

  const resetVehicleForm = () => {
    setVehicleFormData({
      contact_id: '__no_contact__',
      registration_number: '',
      make: '',
      model: '',
      year: '',
      color: '',
      vehicle_type: '',
      body_type: '__not_specified__',
      fuel_type: '__not_specified__',
      transmission: '__not_specified__',
      engine_size: '',
      mileage: '',
      vin_number: '',
      insurance_company: '',
      insurance_renewal_date: '',
      mot_expiry_date: '',
      mot_status: '',
      tax_status: '',
      co2_emissions: '',
      special_requirements: '',
      notes: ''
    });
    setEditingVehicle(null);
  };

  const openVehicleDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setVehicleFormData({
        contact_id: vehicle.contact_id || '__no_contact__',
        registration_number: vehicle.registration_number || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year?.toString() || '',
        color: vehicle.color || '',
        vehicle_type: vehicle.vehicle_type || '',
        body_type: vehicle.body_type || '__not_specified__',
        fuel_type: vehicle.fuel_type || '__not_specified__',
        transmission: vehicle.transmission || '__not_specified__',
        engine_size: vehicle.engine_size || '',
        mileage: vehicle.mileage?.toString() || '',
        vin_number: vehicle.vin_number || '',
        insurance_company: vehicle.insurance_company || '',
        insurance_renewal_date: vehicle.insurance_renewal_date || '',
        mot_expiry_date: vehicle.mot_expiry_date || '',
        mot_status: vehicle.mot_status || '',
        tax_status: vehicle.tax_status || '',
        co2_emissions: vehicle.co2_emissions?.toString() || '',
        special_requirements: vehicle.special_requirements || '',
        notes: vehicle.notes || ''
      });
    } else {
      resetVehicleForm();
    }
    setIsVehicleDialogOpen(true);
  };

  const handleVehicleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!vehicleFormData.registration_number?.trim()) {
      toast.error('Registration number is required');
      return;
    }
    if (!vehicleFormData.make?.trim()) {
      toast.error('Make is required');
      return;
    }
    if (!vehicleFormData.model?.trim()) {
      toast.error('Model is required');
      return;
    }
    if (!vehicleFormData.vehicle_type?.trim()) {
      toast.error('Vehicle type is required');
      return;
    }
    
    try {
      console.log('Submitting vehicle form data:', vehicleFormData); // Debug log
      
      // Build complete payload with all form fields
      const submitData = {
        customer_id: customerId,
        registration_number: vehicleFormData.registration_number.trim(),
        make: vehicleFormData.make.trim(),
        model: vehicleFormData.model.trim(),
        vehicle_type: vehicleFormData.vehicle_type.trim(),
      };

      // Add optional fields if they have values
      if (vehicleFormData.contact_id && vehicleFormData.contact_id !== '__no_contact__') {
        submitData.contact_id = vehicleFormData.contact_id;
      }
      if (vehicleFormData.year) submitData.year = parseInt(vehicleFormData.year);
      if (vehicleFormData.color) submitData.color = vehicleFormData.color.trim();
      if (vehicleFormData.body_type && vehicleFormData.body_type !== '__not_specified__') {
        submitData.body_type = vehicleFormData.body_type;
      }
      if (vehicleFormData.fuel_type && vehicleFormData.fuel_type !== '__not_specified__') {
        submitData.fuel_type = vehicleFormData.fuel_type;
      }
      if (vehicleFormData.transmission && vehicleFormData.transmission !== '__not_specified__') {
        submitData.transmission = vehicleFormData.transmission;
      }
      if (vehicleFormData.engine_size) submitData.engine_size = vehicleFormData.engine_size.trim();
      if (vehicleFormData.mileage) submitData.mileage = parseInt(vehicleFormData.mileage);
      if (vehicleFormData.vin_number) submitData.vin_number = vehicleFormData.vin_number.trim();
      if (vehicleFormData.mot_expiry_date) submitData.mot_expiry_date = vehicleFormData.mot_expiry_date;
      if (vehicleFormData.mot_status) submitData.mot_status = vehicleFormData.mot_status.trim();
      if (vehicleFormData.tax_status) submitData.tax_status = vehicleFormData.tax_status.trim();
      if (vehicleFormData.co2_emissions) submitData.co2_emissions = parseInt(vehicleFormData.co2_emissions);
      if (vehicleFormData.special_requirements) submitData.special_requirements = vehicleFormData.special_requirements.trim();
      if (vehicleFormData.notes) submitData.notes = vehicleFormData.notes.trim();

      // Add company_id - get from customer data or user context
      if (customer?.company_id) {
        submitData.company_id = customer.company_id;
      } else {
        // Try to get company_id from localStorage or user context
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user.company_id) {
            submitData.company_id = user.company_id;
          }
        }
      }

      if (!submitData.company_id) {
        toast.error('Unable to determine company context. Please refresh and try again.');
        return;
      }

      console.log('Final submit data:', submitData); // Debug log
      console.log('API endpoint:', `${API}/customers/${customerId}/vehicles`);
      console.log('Customer ID:', customerId);

      if (editingVehicle) {
        console.log('Updating existing vehicle:', editingVehicle.id);
        await axios.put(`${API}/vehicles/${editingVehicle.id}`, submitData, getAuthHeaders());
        toast.success('Vehicle updated successfully');
      } else {
        console.log('Creating new vehicle for customer:', customerId);
        const response = await axios.post(`${API}/customers/${customerId}/vehicles`, submitData, getAuthHeaders());
        console.log('Vehicle creation response:', response.data);
        toast.success('Vehicle created successfully');
        
        // If vehicle was added from Quick Quote, auto-assign to that item
        if (addVehicleForItemIndex !== null) {
          updateQuickQuoteItem(addVehicleForItemIndex, 'vehicle_id', response.data.id);
          setAddVehicleForItemIndex(null);
        }
      }

      await fetchVehicles();
      setIsVehicleDialogOpen(false);
      resetVehicleForm();
    } catch (error) {
      console.error('Vehicle save error details:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      let errorMessage = 'Failed to save vehicle';
      
      if (error.response?.data) {
        console.log('Full error response data:', JSON.stringify(error.response.data, null, 2));
        
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Handle validation errors array
            errorMessage = error.response.data.detail.map(err => 
              typeof err === 'string' ? err : `${err.loc?.join('.')} - ${err.msg}` || 'Validation error'
            ).join(', ');
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleVehicleDelete = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`, getAuthHeaders());
      toast.success('Vehicle deleted successfully');
      await fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  // Helper function to map DVLA wheelplan to vehicle type
  const mapWheelplanToVehicleType = (wheelplan) => {
    if (!wheelplan) return 'car';
    const plan = wheelplan.toLowerCase();
    
    // Motorcycle detection
    if (plan.includes('2 wheel')) return 'motorcycle';
    
    // Truck/HGV detection
    if (plan.includes('3 axle') || plan.includes('artic')) return 'truck';
    
    // Van detection (smaller rigid body vehicles)
    if (plan.includes('rigid') && !plan.includes('3 axle')) {
      // Could be a van or car, default to car
      return 'car';
    }
    
    // Default to car for most vehicles
    return 'car';
  };

  // Helper function to format engine capacity
  const formatEngineCapacity = (capacity) => {
    if (!capacity) return '';
    const cc = parseInt(capacity);
    if (cc >= 1000) {
      return `${(cc / 1000).toFixed(1)}L`;
    }
    return `${cc}cc`;
  };

  // DVLA lookup function
  const handleDvlaLookup = async () => {
    const regNumber = vehicleFormData.registration_number?.trim();
    
    if (!regNumber) {
      toast.error('Please enter a registration number first');
      return;
    }

    setIsDvlaLookupLoading(true);
    
    try {
      const response = await axios.post(`${API}/vehicles/dvla-lookup`, 
        { registration_number: regNumber }, 
        getAuthHeaders()
      );
      
      console.log('DVLA API Response:', response.data);
      
      if (response.data.success) {
        const dvlaData = response.data.data;
        
        // Helper to handle placeholder values from test environment
        const cleanValue = (value) => {
          if (!value) return '';
          if (typeof value === 'string' && value.includes('<1 YEAR FROM NOW>')) return '';
          return value;
        };
        
        // Map DVLA data to form fields
        const updatedFormData = {
          ...vehicleFormData,
          make: dvlaData.make || vehicleFormData.make,
          color: dvlaData.colour || vehicleFormData.color,
          year: dvlaData.yearOfManufacture?.toString() || vehicleFormData.year,
          fuel_type: dvlaData.fuelType?.toLowerCase() || vehicleFormData.fuel_type,
          engine_size: dvlaData.engineCapacity ? formatEngineCapacity(dvlaData.engineCapacity) : vehicleFormData.engine_size,
          vehicle_type: dvlaData.wheelplan ? mapWheelplanToVehicleType(dvlaData.wheelplan) : vehicleFormData.vehicle_type,
          // MOT fields with special handling
          mot_status: dvlaData.motStatus || vehicleFormData.mot_status || '',
          mot_expiry_date: cleanValue(dvlaData.motExpiryDate) || vehicleFormData.mot_expiry_date,
          // Tax fields
          tax_status: dvlaData.taxStatus || vehicleFormData.tax_status || '',
          // CO2 emissions
          co2_emissions: dvlaData.co2Emissions !== undefined && dvlaData.co2Emissions !== null 
            ? dvlaData.co2Emissions.toString() 
            : vehicleFormData.co2_emissions || '',
        };
        
        setVehicleFormData(updatedFormData);
        
        // Success message with helpful info
        const fieldsPopulated = [];
        if (dvlaData.make) fieldsPopulated.push('Make');
        if (dvlaData.colour) fieldsPopulated.push('Color');
        if (dvlaData.yearOfManufacture) fieldsPopulated.push('Year');
        if (dvlaData.fuelType) fieldsPopulated.push('Fuel Type');
        if (dvlaData.motStatus) fieldsPopulated.push('MOT Status');
        if (dvlaData.taxStatus) fieldsPopulated.push('Tax Status');
        
        toast.success(
          `✅ Vehicle found! Populated: ${fieldsPopulated.join(', ')}`,
          { duration: 4000 }
        );
        
      } else {
        // Handle specific error responses
        handleDvlaError(response.data);
      }
      
    } catch (error) {
      console.error('DVLA lookup error:', error);
      handleDvlaError(error.response?.data);
    } finally {
      setIsDvlaLookupLoading(false);
    }
  };

  // Handle DVLA API errors with user-friendly messages
  const handleDvlaError = (errorData) => {
    if (!errorData) {
      toast.error('❌ Network error - please check your connection');
      return;
    }

    // Use the user_message from backend if available
    if (errorData.user_message) {
      // Map status codes to emoji prefixes for better UX
      const emojiMap = {
        400: '⚠️',
        404: '🔍',
        429: '⏱️',
        500: '❌',
        503: '🔧',
        408: '⏱️'
      };
      const emoji = emojiMap[errorData.status_code] || '⚠️';
      toast.error(`${emoji} ${errorData.user_message}`);
      return;
    }

    // Fallback: Handle legacy error response formats
    if (errorData.errors && errorData.errors.length > 0) {
      const error = errorData.errors[0];
      switch (error.status?.toString()) {
        case '400':
          toast.error('⚠️ Invalid registration number format. Please check and try again.');
          break;
        case '404':
          toast.error('🔍 Vehicle not found in DVLA database. Please enter details manually or check the registration number.');
          break;
        case '500':
          toast.error('❌ DVLA service is temporarily unavailable. Please try again later or enter details manually.');
          break;
        default:
          toast.error(`⚠️ DVLA Error: ${error.detail || 'Unknown error'}`);
      }
    } else if (errorData.message === 'Too Many Requests') {
      toast.error('⏱️ Too many lookups in a short time. Please wait a moment and try again.');
    } else if (errorData.status === '503' || errorData.status === 503) {
      toast.error('🔧 DVLA service is currently down for maintenance. Please enter vehicle details manually for now.');
    } else {
      toast.error('⚠️ Failed to lookup vehicle details. Please try again or enter details manually.');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <Card className="p-12 text-center">
        <p className="text-gray-500">Customer not found</p>
        <Button onClick={() => navigate('/dashboard/customers')} className="mt-4">
          Back to Customers
        </Button>
      </Card>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      dormant: 'bg-yellow-100 text-yellow-700',
      do_not_contact: 'bg-red-100 text-red-700',
      new: 'bg-blue-100 text-blue-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getInsightStatusColor = (flag) => {
    const colors = {
      active: 'bg-green-100 text-green-700',
      dormant: 'bg-yellow-100 text-yellow-700',
      lost: 'bg-red-100 text-red-700',
      new: 'bg-blue-100 text-blue-700'
    };
    return colors[flag] || 'bg-gray-100 text-gray-700';
  };

  const openEditDialog = () => {
    setEditFormData({
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      address_1: customer.address_1 || customer.address || '',
      address_2: customer.address_2 || '',
      address_3: customer.address_3 || '',
      city: customer.city || '',
      county: customer.county || '',
      postcode: customer.postcode || '',
      customer_type: customer.customer_type,
      tags: customer.tags || [],
      source: customer.source || '',
      status: customer.status,
      notes: customer.notes || '',
      email_opt_in: customer.email_opt_in ?? true,
      sms_opt_in: customer.sms_opt_in ?? true,
      whatsapp_opt_in: customer.whatsapp_opt_in ?? false
    });
    setTagInput('');
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/customers/${customerId}`, editFormData, getAuthHeaders());
      setCustomer(response.data);
      setIsEditDialogOpen(false);
      toast.success('Customer updated successfully');
    } catch (error) {
      console.error('Error updating customer:', error);
      
      let errorMessage = 'Failed to update customer';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            // Handle Pydantic validation errors
            errorMessage = error.response.data.detail
              .map(err => `${err.loc ? err.loc.join(' ') : 'Field'}: ${err.msg}`)
              .join(', ');
          } else {
            errorMessage = error.response.data.detail;
          }
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const addTag = () => {
    if (tagInput && !editFormData.tags.includes(tagInput)) {
      setEditFormData({ ...editFormData, tags: [...editFormData.tags, tagInput] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setEditFormData({ ...editFormData, tags: editFormData.tags.filter(t => t !== tag) });
  };


  // Communication Log handlers
  const openCommLogDialog = (contact = null, logToEdit = null) => {
    // Reset custom contact mode
    setIsCustomContactName(false);
    
    if (contact) {
      // Quick add from contact - pre-fill contact details
      const now = new Date();
      const dateTimeLocal = now.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
      setCommLogFormData({
        contact_name: `${contact.first_name} ${contact.last_name}`,
        date: dateTimeLocal,
        notes: '',
        next_callback_date: '',
        communication_method: '',
        contact_made: false,
        company_name: customer.name || '',
        company_phone: customer.phone || '',
        contact_phone: contact.phone || '',
        contact_email: contact.email || ''
      });
    } else if (logToEdit) {
      // Edit existing log - use passed log parameter
      // Check if contact_name matches customer or any contact
      const isCustomerName = logToEdit.contact_name === customer.name;
      const isKnownContact = contacts.some(c => `${c.first_name} ${c.last_name}` === logToEdit.contact_name);
      
      // If it's not customer or known contact, it's a custom name
      if (!isCustomerName && !isKnownContact) {
        setIsCustomContactName(true);
      }
      
      setCommLogFormData({
        contact_name: logToEdit.contact_name,
        date: logToEdit.date.slice(0, 16), // Convert to datetime-local format
        notes: logToEdit.notes,
        next_callback_date: logToEdit.next_callback_date || '',
        communication_method: logToEdit.communication_method || '',
        contact_made: logToEdit.contact_made || false,
        company_name: logToEdit.company_name || '',
        company_phone: logToEdit.company_phone || '',
        contact_phone: logToEdit.contact_phone || '',
        contact_email: logToEdit.contact_email || ''
      });
    } else {
      // New log
      const now = new Date();
      const dateTimeLocal = now.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
      setCommLogFormData({
        contact_name: '',
        date: dateTimeLocal,
        notes: '',
        next_callback_date: '',
        communication_method: '',
        contact_made: false,
        company_name: customer.name || '',
        company_phone: customer.phone || '',
        contact_phone: '',
        contact_email: ''
      });
    }
    setIsCommLogDialogOpen(true);
    
    // Auto-focus notes field when quick-adding from contact
    if (contact) {
      setTimeout(() => {
        notesInputRef.current?.focus();
      }, 100);
    }
  };

  const handleCommLogSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const logData = {
        customer_id: customerId,
        ...commLogFormData
      };

      if (editingCommLog) {
        // Update existing log
        await axios.put(`${API}/communication_logs/${editingCommLog.id}`, commLogFormData, getAuthHeaders());
        toast.success('Communication log updated successfully');
      } else {
        // Create new log
        await axios.post(`${API}/communication_logs`, logData, getAuthHeaders());
        toast.success('Communication log created successfully');
      }

      setIsCommLogDialogOpen(false);
      setEditingCommLog(null);
      await fetchCommunicationLogs();
    } catch (error) {
      console.error('Error saving communication log:', error);
      toast.error('Failed to save communication log');
    }
  };

  const handleEditCommLog = (log) => {
    setEditingCommLog(log);
    // Pass log directly instead of relying on state update
    openCommLogDialog(null, log);
  };

  const handleDeleteCommLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this communication log?')) return;
    
    try {
      await axios.delete(`${API}/communication_logs/${logId}`, getAuthHeaders());
      toast.success('Communication log deleted successfully');
      await fetchCommunicationLogs();
    } catch (error) {
      console.error('Error deleting communication log:', error);
      toast.error('Failed to delete communication log');
    }
  };

  const handleNotesClick = (log) => {
    setEditingNotesId(log.id);
    setEditingNotesValue(log.notes);
  };

  const handleNotesSave = async (logId) => {
    try {
      await axios.put(`${API}/communication_logs/${logId}`, 
        { notes: editingNotesValue }, 
        getAuthHeaders()
      );
      toast.success('Notes updated successfully');
      setEditingNotesId(null);
      await fetchCommunicationLogs();
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error('Failed to update notes');
    }
  };

  const handleNotesCancel = () => {
    setEditingNotesId(null);
    setEditingNotesValue('');
  };

  const handleDateClick = (log) => {
    setEditingDateId(log.id);
    setEditingDateValue(log.date.slice(0, 16)); // Convert to datetime-local format
  };

  const handleDateSave = async (logId) => {
    try {
      await axios.put(`${API}/communication_logs/${logId}`, 
        { date: editingDateValue }, 
        getAuthHeaders()
      );
      toast.success('Date updated successfully');
      setEditingDateId(null);
      await fetchCommunicationLogs();
    } catch (error) {
      console.error('Error updating date:', error);
      toast.error('Failed to update date');
    }
  };

  const handleDateCancel = () => {
    setEditingDateId(null);
    setEditingDateValue('');
  };

  const handleCallbackClick = (log) => {
    setEditingCallbackId(log.id);
    setEditingCallbackValue(log.next_callback_date || '');
  };

  const handleCallbackSave = async (logId) => {
    try {
      await axios.put(`${API}/communication_logs/${logId}`, 
        { next_callback_date: editingCallbackValue || null }, 
        getAuthHeaders()
      );
      toast.success('Callback date updated successfully');
      setEditingCallbackId(null);
      await fetchCommunicationLogs();
    } catch (error) {
      console.error('Error updating callback date:', error);
      toast.error('Failed to update callback date');
    }
  };

  const handleCallbackCancel = () => {
    setEditingCallbackId(null);
    setEditingCallbackValue('');
  };

  const handleMethodClick = (log) => {
    setEditingMethodId(log.id);
    setEditingMethodValue(log.communication_method || '');
  };

  const handleMethodSave = async (logId) => {
    try {
      await axios.put(`${API}/communication_logs/${logId}`, 
        { communication_method: editingMethodValue || null }, 
        getAuthHeaders()
      );
      toast.success('Communication method updated successfully');
      setEditingMethodId(null);
      await fetchCommunicationLogs();
    } catch (error) {
      console.error('Error updating method:', error);
      toast.error('Failed to update method');
    }
  };

  const handleMethodCancel = () => {
    setEditingMethodId(null);
    setEditingMethodValue('');
  };

  const handleContactNameClick = (log) => {
    setEditingContactNameId(log.id);
    setEditingContactNameValue(log.contact_name);
    setIsCustomContactNameInline(false); // Reset custom mode
  };

  const handleContactNameSave = async (logId) => {
    try {
      await axios.put(`${API}/communication_logs/${logId}`, 
        { contact_name: editingContactNameValue }, 
        getAuthHeaders()
      );
      toast.success('Contact name updated successfully');
      setEditingContactNameId(null);
      setIsCustomContactNameInline(false);
      await fetchCommunicationLogs();
    } catch (error) {
      console.error('Error updating contact name:', error);
      toast.error('Failed to update contact name');
    }
  };

  const handleContactNameCancel = () => {
    setEditingContactNameId(null);
    setEditingContactNameValue('');
    setIsCustomContactNameInline(false);
  };




  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCommLogs = [...communicationLogs].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    }
    return 0;
  });

  // Find the most recent log by actual date (not by position in sorted array)
  const mostRecentLogId = communicationLogs.length > 0
    ? communicationLogs.reduce((newest, log) => 
        new Date(log.date) > new Date(newest.date) ? log : newest
      ).id
    : null;


  // Helper function to get communication method icon
  const getCommunicationIcon = (method) => {
    const iconProps = { className: "w-5 h-5" };
    
    switch(method?.toLowerCase()) {
      case 'phone':
        return <Phone {...iconProps} className="w-5 h-5 text-blue-600" />;
      case 'email':
        return <Mail {...iconProps} className="w-5 h-5 text-green-600" />;
      case 'sms':
        return <MessageSquare {...iconProps} className="w-5 h-5 text-purple-600" />;
      case 'whatsapp':
        return <MessageSquare {...iconProps} className="w-5 h-5 text-green-500" />;
      case 'in-person':
        return <User {...iconProps} className="w-5 h-5 text-orange-600" />;
      default:
        return <MessageSquare {...iconProps} className="w-5 h-5 text-gray-400" />;
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard/customers')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>
              {customer.name}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={getStatusColor(customer.status)}>
                {customer.status}
              </Badge>
              {insights && (
                <Badge className={getInsightStatusColor(insights.status_flag)}>
                  Intelligence: {insights.status_flag}
                </Badge>
              )}
              <Badge variant="outline">{customer.customer_type}</Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="default" className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600" onClick={() => openQuickQuote()}>
            <Zap className="w-4 h-4" />
            Quick Quote
          </Button>
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" onClick={openEditDialog}>
                <Edit className="w-4 h-4" />
                Edit Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Customer</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="edit-name">Customer Name *</Label>
                    <Input
                      id="edit-name"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-email">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-phone">Phone</Label>
                    <Input
                      id="edit-phone"
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="edit-address_1">Address Line 1</Label>
                    <Input
                      id="edit-address_1"
                      value={editFormData.address_1}
                      onChange={(e) => setEditFormData({ ...editFormData, address_1: e.target.value })}
                      placeholder="House number and street name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-address_2">Address Line 2</Label>
                    <Input
                      id="edit-address_2"
                      value={editFormData.address_2}
                      onChange={(e) => setEditFormData({ ...editFormData, address_2: e.target.value })}
                      placeholder="Area or district (optional)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-address_3">Address Line 3</Label>
                    <Input
                      id="edit-address_3"
                      value={editFormData.address_3}
                      onChange={(e) => setEditFormData({ ...editFormData, address_3: e.target.value })}
                      placeholder="Additional address info (optional)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-city">City/Town</Label>
                    <Input
                      id="edit-city"
                      value={editFormData.city}
                      onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                      placeholder="e.g., London, Manchester"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-county">County</Label>
                    <Select value={editFormData.county} onValueChange={(value) => setEditFormData({ ...editFormData, county: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select county" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {counties.map(county => (
                          <SelectItem key={county} value={county}>{county}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit-postcode">Postcode</Label>
                    <Input
                      id="edit-postcode"
                      value={editFormData.postcode}
                      onChange={(e) => setEditFormData({ ...editFormData, postcode: e.target.value })}
                      placeholder="SW1A 1AA"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-customer_type">Type</Label>
                    <Select value={editFormData.customer_type} onValueChange={(value) => setEditFormData({ ...editFormData, customer_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit-source">Source</Label>
                    <Select value={editFormData.source} onValueChange={(value) => setEditFormData({ ...editFormData, source: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Google">Google</SelectItem>
                        <SelectItem value="Facebook">Facebook</SelectItem>
                        <SelectItem value="Checkatrade">Checkatrade</SelectItem>
                        <SelectItem value="Referral">Referral</SelectItem>
                        <SelectItem value="Direct">Direct</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit-status">Status</Label>
                    <Select value={editFormData.status} onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="dormant">Dormant</SelectItem>
                        <SelectItem value="do_not_contact">Do Not Contact</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Tags</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Add tag (e.g., fleet, ceramic)"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button type="button" size="sm" onClick={addTag}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {editFormData.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                        {tag} ×
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="edit-notes">Notes</Label>
                  <textarea
                    id="edit-notes"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    className="w-full min-h-20 px-3 py-2 border rounded-md"
                    placeholder="Additional notes about this customer..."
                  />
                </div>
                
                <div>
                  <Label>Consent (GDPR)</Label>
                  <div className="space-y-2 mt-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editFormData.email_opt_in}
                        onChange={(e) => setEditFormData({ ...editFormData, email_opt_in: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm">Email Marketing</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editFormData.sms_opt_in}
                        onChange={(e) => setEditFormData({ ...editFormData, sms_opt_in: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm">SMS Marketing</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editFormData.whatsapp_opt_in}
                        onChange={(e) => setEditFormData({ ...editFormData, whatsapp_opt_in: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm">WhatsApp Marketing</span>
                    </label>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    Update Customer
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Customer Info Card */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Contact Information</h3>
            <div className="space-y-2">
              {customer.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span>{customer.email}</span>
                </div>
              )}
              {customer.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>{customer.phone}</span>
                </div>
              )}
              {(customer.address_1 || customer.address || customer.city) && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    {customer.address_1 && <div>{customer.address_1}</div>}
                    {customer.address_2 && <div>{customer.address_2}</div>}
                    {customer.address_3 && <div>{customer.address_3}</div>}
                    {!customer.address_1 && customer.address && <div>{customer.address}</div>}
                    {(customer.city || customer.county) && (
                      <div>
                        {customer.city}
                        {customer.city && customer.county && ', '}
                        {customer.county}
                      </div>
                    )}
                    {customer.postcode && <div className="font-mono text-xs">{customer.postcode}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {insights && (
            <>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Customer Value</h3>
                <div className="space-y-2">
                  <div className="text-2xl font-bold text-green-600">
                    £{insights.total_lifetime_spend.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-600">
                    <div>Jobs: {insights.job_count}</div>
                    <div>Avg per job: £{insights.average_spend_per_job.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Activity</h3>
                <div className="space-y-2 text-sm">
                  {insights.last_job_date ? (
                    <>
                      <div>Last job: {new Date(insights.last_job_date).toLocaleDateString()}</div>
                      <div>Days ago: {insights.days_since_last_job}</div>
                      {insights.average_gap_days && (
                        <div>Avg frequency: {Math.round(insights.average_gap_days)} days</div>
                      )}
                    </>
                  ) : (
                    <div className="text-gray-500">No completed jobs yet</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {customer.tags && customer.tags.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {customer.tags.map(tag => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
          </div>
        )}

        {insights && insights.high_value_services && insights.high_value_services.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-500 mb-2">High-Value Services</h3>
            <div className="flex flex-wrap gap-2">
              {insights.high_value_services.map(service => (
                <Badge key={service} className="bg-purple-100 text-purple-700">{service}</Badge>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Contacts Section */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5" />
            Contacts for {customer.name}
          </h3>
        </div>
        <ContactsManagement 
          getAuthHeaders={getAuthHeaders} 
          customerId={customerId}
          customerName={customer.name}
          layout="table"
          maxHeight="max-h-80"
          onContactAdded={fetchContacts}
          onQuickQuote={openQuickQuote}
          onQuickCommLog={openCommLogDialog}
        />
      </Card>

      {/* Communications Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Communication Log
          </h3>
          <Button onClick={() => {
            setEditingCommLog(null);
            openCommLogDialog();
          }} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Communication
          </Button>
        </div>
        
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="w-[150px] cursor-pointer hover:bg-gray-50" 
                  onClick={() => handleSort('date')}
                >
                  Date {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead 
                  className="w-[60px] text-center"
                >
                  Method
                </TableHead>
                <TableHead 
                  className="w-[200px] cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('contact_name')}
                >
                  Contact Name {sortConfig.key === 'contact_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead className="w-auto">Notes</TableHead>
                <TableHead 
                  className="w-[150px] cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('next_callback_date')}
                >
                  Next Call Back Date {sortConfig.key === 'next_callback_date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCommLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No communication logs yet</p>
                    <p className="text-sm">Click "Add Communication" to create your first log entry</p>
                  </TableCell>
                </TableRow>
              ) : (
                sortedCommLogs.map((log) => (
                  <TableRow 
                    key={log.id}
                    className={log.id === mostRecentLogId ? 'bg-blue-50 hover:bg-blue-100' : ''}
                  >
                    <TableCell className="font-medium">
                      {editingDateId === log.id ? (
                        <div className="space-y-2">
                          <Input
                            type="datetime-local"
                            value={editingDateValue}
                            onChange={(e) => setEditingDateValue(e.target.value)}
                            className="border-blue-500"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleDateSave(log.id)}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleDateCancel}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:bg-gray-50 p-2 rounded"
                          onClick={() => handleDateClick(log)}
                          title="Click to edit date & time"
                        >
                          <div>{new Date(log.date).toLocaleDateString('en-GB')}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(log.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {editingMethodId === log.id ? (
                        <div className="space-y-2 px-2">
                          <Select
                            value={editingMethodValue}
                            onValueChange={(value) => setEditingMethodValue(value)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select method" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="phone">Phone</SelectItem>
                              <SelectItem value="email">Email</SelectItem>
                              <SelectItem value="sms">SMS</SelectItem>
                              <SelectItem value="whatsapp">WhatsApp</SelectItem>
                              <SelectItem value="in-person">In Person</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleMethodSave(log.id)}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleMethodCancel}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="flex justify-center cursor-pointer hover:bg-gray-50 p-2 rounded" 
                          title={`${log.communication_method || 'Not specified'} - Click to edit`}
                          onClick={() => handleMethodClick(log)}
                        >
                          {getCommunicationIcon(log.communication_method)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingContactNameId === log.id ? (
                        <div className="space-y-2">
                          {(contacts.length > 0 || customer.customer_type === 'individual') && !isCustomContactNameInline ? (
                            <Select
                              value={
                                // Convert contact_name back to the correct value for the dropdown
                                editingContactNameValue === customer.name ? '__customer__' :
                                editingContactNameValue
                              }
                              onValueChange={(value) => {
                                if (value === '__custom__') {
                                  setIsCustomContactNameInline(true);
                                  setEditingContactNameValue('');
                                } else if (value === '__customer__') {
                                  // Customer themselves (for individual customers)
                                  setEditingContactNameValue(customer.name);
                                } else {
                                  // Find the contact and use their name
                                  const contact = contacts.find(c => `${c.first_name} ${c.last_name}` === value);
                                  if (contact) {
                                    setEditingContactNameValue(value);
                                  } else {
                                    setEditingContactNameValue(value);
                                  }
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select contact" />
                              </SelectTrigger>
                              <SelectContent>
                                {customer.customer_type === 'individual' && (
                                  <SelectItem value="__customer__">
                                    {customer.name} (Customer)
                                  </SelectItem>
                                )}
                                {contacts.map((contact) => (
                                  <SelectItem key={contact.id} value={`${contact.first_name} ${contact.last_name}`}>
                                    {contact.first_name} {contact.last_name} {contact.role && `(${contact.role})`}
                                  </SelectItem>
                                ))}
                                <SelectItem value="__custom__">Other (Type Custom Name)</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={editingContactNameValue}
                              onChange={(e) => setEditingContactNameValue(e.target.value)}
                              className="border-blue-500"
                              placeholder="Enter contact name"
                              autoFocus
                            />
                          )}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleContactNameSave(log.id)}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleContactNameCancel}
                            >
                              Cancel
                            </Button>
                            {(contacts.length > 0 || customer.customer_type === 'individual') && isCustomContactNameInline && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setIsCustomContactNameInline(false);
                                  setEditingContactNameValue('');
                                }}
                              >
                                Choose from contacts
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:bg-gray-50 p-2 rounded"
                          onClick={() => handleContactNameClick(log)}
                          title="Click to edit contact name"
                        >
                          {log.contact_name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-md">
                      {editingNotesId === log.id ? (
                        <div className="space-y-2">
                          <textarea
                            className="w-full min-h-[80px] px-3 py-2 border border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                            value={editingNotesValue}
                            onChange={(e) => setEditingNotesValue(e.target.value)}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleNotesSave(log.id)}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleNotesCancel}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:bg-gray-50 p-2 rounded relative min-h-[4.5em]"
                          onClick={() => handleNotesClick(log)}
                          title="Click to edit notes"
                        >
                          <div 
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: '3',
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {log.notes}
                          </div>
                          {log.notes && log.notes.length > 150 && (
                            <div className="absolute bottom-1 right-1 bg-gray-100 rounded-full p-1">
                              <MoreHorizontal className="w-3 h-3 text-gray-500" />
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingCallbackId === log.id ? (
                        <div className="space-y-2">
                          <Input
                            type="date"
                            value={editingCallbackValue}
                            onChange={(e) => setEditingCallbackValue(e.target.value)}
                            className="border-blue-500"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleCallbackSave(log.id)}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCallbackCancel}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:bg-gray-50 p-2 rounded flex items-center gap-2"
                          onClick={() => handleCallbackClick(log)}
                          title="Click to edit callback date"
                        >
                          {log.next_callback_date ? (
                            new Date(log.next_callback_date).toLocaleDateString('en-GB')
                          ) : (
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm">Completed</span>
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditCommLog(log)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCommLog(log.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Tabs for other sections */}
      <Tabs defaultValue="vehicles" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="vehicles" className="gap-2">
            <Car className="w-4 h-4" />
            Vehicles
          </TabsTrigger>
          <TabsTrigger value="jobs" className="gap-2">
            <Briefcase className="w-4 h-4" />
            Quotes, Orders & Jobs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vehicles" className="mt-6">
          <div className="space-y-6">
            {/* Vehicle Management Header */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Vehicles</h3>
                <p className="text-gray-600">Manage vehicles for {customer.name}</p>
              </div>
              <Button onClick={() => openVehicleDialog()} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Vehicle
              </Button>
            </div>

            {/* Vehicles List */}
            {vehicles.length === 0 ? (
              <Card className="p-12 text-center">
                <Car className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No vehicles registered yet</p>
                <p className="text-xs text-gray-400 mt-2">
                  Add a vehicle to get started with vehicle management
                </p>
                <Button onClick={() => openVehicleDialog()} className="mt-4 gap-2">
                  <Plus className="w-4 h-4" />
                  Add First Vehicle
                </Button>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Registration</TableHead>
                      <TableHead>Make & Model</TableHead>
                      <TableHead>Year</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Next Service</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicles.map((vehicle) => (
                      <TableRow key={vehicle.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{getVehicleTypeIcon(vehicle.vehicle_type)}</span>
                            <div>
                              <div className="font-mono font-medium">
                                {vehicle.registration_number}
                              </div>
                              {vehicle.color && (
                                <div className="text-xs text-gray-500">{vehicle.color}</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        
                        <TableCell>
                          <div className="font-medium">
                            {vehicle.make} {vehicle.model}
                          </div>
                          {vehicle.engine_size && (
                            <div className="text-xs text-gray-500">{vehicle.engine_size}</div>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          {vehicle.year || <span className="text-gray-400">-</span>}
                        </TableCell>
                        
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {vehicle.vehicle_type}
                          </Badge>
                        </TableCell>
                        
                        <TableCell>
                          {vehicle.contact_id ? (
                            <div className="text-sm">
                              {(() => {
                                const contact = contacts.find(c => c.id === vehicle.contact_id);
                                if (!contact) return 'Contact not found';
                                const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                                return fullName || 'Unnamed Contact';
                              })()}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">No contact assigned</span>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          {vehicle.next_service_due ? (
                            <div className="text-sm">
                              {new Date(vehicle.next_service_due).toLocaleDateString()}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">Not scheduled</span>
                          )}
                        </TableCell>
                        
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openVehicleDialog(vehicle)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleVehicleDelete(vehicle.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="jobs" className="mt-6">
          <div className="space-y-6">
            {/* Summary Cards with Totals */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Quotes</p>
                    <p className="text-2xl font-bold">{quotes.filter(q => q.status !== 'accepted' && q.status !== 'converted').length}</p>
                    <p className="text-sm text-green-600 font-semibold mt-1">
                      £{quotes.filter(q => q.status !== 'accepted' && q.status !== 'converted')
                        .reduce((sum, q) => sum + (q.total || 0), 0).toFixed(2)}
                    </p>
                  </div>
                  <Badge variant="outline">Pending</Badge>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Orders</p>
                    <p className="text-2xl font-bold">{quotes.filter(q => q.status === 'accepted').length}</p>
                    <p className="text-sm text-green-600 font-semibold mt-1">
                      £{quotes.filter(q => q.status === 'accepted')
                        .reduce((sum, q) => sum + (q.total || 0), 0).toFixed(2)}
                    </p>
                  </div>
                  <Badge className="bg-green-100 text-green-700">Accepted</Badge>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Jobs</p>
                    <p className="text-2xl font-bold">{jobs.length}</p>
                    <p className="text-sm text-green-600 font-semibold mt-1">
                      £{jobs.reduce((sum, j) => sum + (j.total || 0), 0).toFixed(2)}
                    </p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700">All Jobs</Badge>
                </div>
              </Card>
            </div>

            {/* Enhanced Quotes Section */}
            {quotes.filter(q => q.status !== 'accepted' && q.status !== 'converted').length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Quotes
                </h3>
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[130px]">Quote #</TableHead>
                        <TableHead className="w-[150px]">Contact</TableHead>
                        <TableHead className="w-[100px]">Vehicles</TableHead>
                        <TableHead className="w-[120px]">Status</TableHead>
                        <TableHead className="w-[120px]">Date</TableHead>
                        <TableHead className="w-[100px]">Postcode</TableHead>
                        <TableHead className="w-[110px] text-right">Total</TableHead>
                        <TableHead className="w-[100px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quotes
                        .filter(q => q.status !== 'accepted' && q.status !== 'converted')
                        .map((quote) => (
                          <TableRow key={quote.id} className="hover:bg-gray-50">
                            <TableCell>
                              <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                                {quote.quote_number}
                              </code>
                            </TableCell>
                            <TableCell className="text-sm">
                              {quote.contact_name || <span className="text-gray-400">No contact</span>}
                            </TableCell>
                            <TableCell>
                              {quote.vehicles && quote.vehicles.length > 0 ? (
                                <div className="flex items-center gap-1">
                                  <Car className="w-4 h-4 text-gray-500" />
                                  <span className="text-sm">{quote.vehicles.length}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={quote.status === 'sent' ? 'default' : 'outline'}
                                className="capitalize"
                              >
                                {quote.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {new Date(quote.created_at).toLocaleDateString('en-GB')}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {quote.postcode || <span className="text-gray-400">-</span>}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-green-600">
                              £{quote.total?.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/dashboard/quotes`)}
                                  title="View Quote"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/dashboard/quotes`)}
                                  title="Print Quote"
                                >
                                  <Printer className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}

            {/* Enhanced Orders Section */}
            {quotes.filter(q => q.status === 'accepted').length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Orders (Accepted Quotes)
                </h3>
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[130px]">Order #</TableHead>
                        <TableHead className="w-[150px]">Contact</TableHead>
                        <TableHead className="w-[100px]">Vehicles</TableHead>
                        <TableHead className="w-[120px]">Status</TableHead>
                        <TableHead className="w-[120px]">Accepted</TableHead>
                        <TableHead className="w-[100px]">Postcode</TableHead>
                        <TableHead className="w-[110px] text-right">Total</TableHead>
                        <TableHead className="w-[100px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quotes
                        .filter(q => q.status === 'accepted')
                        .map((order) => (
                          <TableRow key={order.id} className="hover:bg-gray-50">
                            <TableCell>
                              <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                                {order.quote_number}
                              </code>
                            </TableCell>
                            <TableCell className="text-sm">
                              {order.contact_name || <span className="text-gray-400">No contact</span>}
                            </TableCell>
                            <TableCell>
                              {order.vehicles && order.vehicles.length > 0 ? (
                                <div className="flex items-center gap-1">
                                  <Car className="w-4 h-4 text-gray-500" />
                                  <span className="text-sm">{order.vehicles.length}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-green-100 text-green-700">Accepted</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {order.accepted_at ? new Date(order.accepted_at).toLocaleDateString('en-GB') : '-'}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {order.postcode || <span className="text-gray-400">-</span>}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-green-600">
                              £{order.total?.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/dashboard/orders`)}
                                  title="View Order"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/dashboard/orders`)}
                                  title="Schedule Job"
                                >
                                  <Calendar className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}

            {/* Enhanced Jobs Section */}
            {jobs.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  Jobs
                </h3>
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[130px]">Job #</TableHead>
                        <TableHead className="w-[150px]">Contact</TableHead>
                        <TableHead className="w-[100px]">Vehicles</TableHead>
                        <TableHead className="w-[120px]">Status</TableHead>
                        <TableHead className="w-[120px]">Scheduled</TableHead>
                        <TableHead className="w-[100px]">Postcode</TableHead>
                        <TableHead className="w-[110px] text-right">Total</TableHead>
                        <TableHead className="w-[100px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobs.map((job) => (
                        <TableRow key={job.id} className="hover:bg-gray-50">
                          <TableCell>
                            <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                              {job.job_number}
                            </code>
                          </TableCell>
                          <TableCell className="text-sm">
                            {job.contact_name || <span className="text-gray-400">No contact</span>}
                          </TableCell>
                          <TableCell>
                            {job.vehicle_ids && job.vehicle_ids.length > 0 ? (
                              <div className="flex items-center gap-1">
                                <Car className="w-4 h-4 text-gray-500" />
                                <span className="text-sm">{job.vehicle_ids.length}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                job.status === 'completed' ? 'success' :
                                job.status === 'in_progress' ? 'default' :
                                'outline'
                              }
                              className="capitalize"
                            >
                              {job.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {job.scheduled_start ? (
                              <div>
                                <div>{new Date(job.scheduled_start).toLocaleDateString('en-GB')}</div>
                                <div className="text-xs text-gray-500">
                                  {new Date(job.scheduled_start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">Not scheduled</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {job.postcode || <span className="text-gray-400">-</span>}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-green-600">
                            £{job.total?.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/dashboard/jobs`)}
                                title="View Job"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/dashboard/jobs`)}
                                title="Update Status"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}

            {/* Empty State */}
            {quotes.length === 0 && jobs.length === 0 && (
              <Card className="p-12 text-center">
                <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No quotes, orders, or jobs yet</p>
                <p className="text-xs text-gray-400 mt-2">
                  Create a quote from the Quotes section to get started
                </p>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Vehicle Management Dialog */}
      <Dialog open={isVehicleDialogOpen} onOpenChange={(open) => { setIsVehicleDialogOpen(open); if (!open) resetVehicleForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleVehicleSubmit} className="space-y-6">
            {/* Basic Vehicle Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="registration_number">Registration Number *</Label>
                <div className="flex gap-2">
                  <Input
                    id="registration_number"
                    value={vehicleFormData.registration_number}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, registration_number: e.target.value.toUpperCase().trim() })}
                    placeholder="AB12 CDE"
                    required
                    className="font-mono flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDvlaLookup}
                    disabled={isDvlaLookupLoading}
                    className="px-3"
                    title="Lookup vehicle details from DVLA"
                  >
                    {isDvlaLookupLoading ? (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  value={vehicleFormData.make}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, make: e.target.value.trim() })}
                  placeholder="e.g., BMW, Ford, Toyota"
                  required
                  className={vehicleFormData.make ? 'border-green-500 bg-green-50' : 'border-orange-300'}
                />
              </div>
              
              <div>
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  value={vehicleFormData.model}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, model: e.target.value.trim() })}
                  placeholder="e.g., Focus, 3 Series, Corolla"
                  required
                  className={vehicleFormData.model ? 'border-green-500 bg-green-50' : 'border-orange-300'}
                />
              </div>
              
              <div>
                <Label htmlFor="year">Year</Label>
                <Input
                  id="year"
                  type="number"
                  value={vehicleFormData.year}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, year: e.target.value })}
                  placeholder="2020"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  className={vehicleFormData.year ? 'border-green-500 bg-green-50' : 'border-orange-300'}
                />
              </div>
              
              <div>
                <Label htmlFor="color">Color</Label>
                <Input
                  id="color"
                  value={vehicleFormData.color}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, color: e.target.value })}
                  placeholder="e.g., Red, Blue, Silver"
                  className={vehicleFormData.color ? 'border-green-500 bg-green-50' : 'border-orange-300'}
                />
              </div>
              
              <div>
                <Label htmlFor="vehicle_type">Vehicle Type *</Label>
                <Select value={vehicleFormData.vehicle_type} onValueChange={(value) => setVehicleFormData({ ...vehicleFormData, vehicle_type: value })} required>
                  <SelectTrigger className={vehicleFormData.vehicle_type ? 'border-green-500 bg-green-50' : 'border-orange-300'}>
                    <SelectValue placeholder="Select vehicle type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">Car</SelectItem>
                    <SelectItem value="van">Van</SelectItem>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="motorcycle">Motorcycle</SelectItem>
                    <SelectItem value="motorhome">Motorhome</SelectItem>
                    <SelectItem value="suv">SUV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Technical Details */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Technical Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="body_type">Body Type</Label>
                  <Select value={vehicleFormData.body_type} onValueChange={(value) => setVehicleFormData({ ...vehicleFormData, body_type: value })}>
                    <SelectTrigger className={vehicleFormData.body_type && vehicleFormData.body_type !== '__not_specified__' ? 'border-green-500 bg-green-50' : 'border-orange-300'}>
                      <SelectValue placeholder="Select body type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__not_specified__">Not specified</SelectItem>
                      <SelectItem value="sedan">Sedan</SelectItem>
                      <SelectItem value="hatchback">Hatchback</SelectItem>
                      <SelectItem value="estate">Estate</SelectItem>
                      <SelectItem value="coupe">Coupe</SelectItem>
                      <SelectItem value="saloon">Saloon</SelectItem>
                      <SelectItem value="mpv">MPV</SelectItem>
                      <SelectItem value="convertible_soft_top">Convertible Soft Top</SelectItem>
                      <SelectItem value="convertible_hard_top">Convertible Hard Top</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="fuel_type">Fuel Type</Label>
                  <Select value={vehicleFormData.fuel_type} onValueChange={(value) => setVehicleFormData({ ...vehicleFormData, fuel_type: value })}>
                    <SelectTrigger className={vehicleFormData.fuel_type && vehicleFormData.fuel_type !== '__not_specified__' ? 'border-green-500 bg-green-50' : 'border-orange-300'}>
                      <SelectValue placeholder="Select fuel type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__not_specified__">Not specified</SelectItem>
                      <SelectItem value="petrol">Petrol</SelectItem>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="electric">Electric</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="plugin_hybrid">Plug-in Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="transmission">Transmission</Label>
                  <Select value={vehicleFormData.transmission} onValueChange={(value) => setVehicleFormData({ ...vehicleFormData, transmission: value })}>
                    <SelectTrigger className={vehicleFormData.transmission && vehicleFormData.transmission !== '__not_specified__' ? 'border-green-500 bg-green-50' : 'border-orange-300'}>
                      <SelectValue placeholder="Select transmission" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__not_specified__">Not specified</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="automatic">Automatic</SelectItem>
                      <SelectItem value="semi_automatic">Semi-Automatic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="engine_size">Engine Size</Label>
                  <Input
                    id="engine_size"
                    value={vehicleFormData.engine_size}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, engine_size: e.target.value })}
                    placeholder="e.g., 2.0L, 1600cc"
                    className={vehicleFormData.engine_size ? 'border-green-500 bg-green-50' : 'border-orange-300'}
                  />
                </div>
              </div>
            </div>

            {/* Contact & Tracking */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Contact & Tracking</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_id">Associated Contact</Label>
                  <Select value={vehicleFormData.contact_id} onValueChange={(value) => setVehicleFormData({ ...vehicleFormData, contact_id: value })}>
                    <SelectTrigger className={vehicleFormData.contact_id && vehicleFormData.contact_id !== '__no_contact__' ? 'border-green-500 bg-green-50' : 'border-gray-300'}>
                      <SelectValue placeholder="Select contact (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__no_contact__">No contact assigned</SelectItem>
                      {contacts.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed Contact'} {contact.role && `(${contact.role})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="mileage">Current Mileage</Label>
                  <Input
                    id="mileage"
                    type="number"
                    value={vehicleFormData.mileage}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, mileage: e.target.value })}
                    placeholder="Miles/KM"
                    className={vehicleFormData.mileage ? 'border-green-500 bg-green-50' : 'border-gray-300'}
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Status & Compliance */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-900">Vehicle Status & Compliance</h4>
                {vehicleFormData.registration_number && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleDvlaLookup}
                    disabled={isDvlaLookupLoading}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${isDvlaLookupLoading ? 'animate-spin' : ''}`} />
                    Refresh from DVLA
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="mot_status">MOT Status</Label>
                  <Input
                    id="mot_status"
                    value={vehicleFormData.mot_status}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, mot_status: e.target.value })}
                    placeholder="e.g., Valid, Not valid"
                    readOnly
                    className={vehicleFormData.mot_status ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-orange-300'}
                  />
                </div>
                
                <div>
                  <Label htmlFor="mot_expiry_date">MOT Expiry Date</Label>
                  <Input
                    id="mot_expiry_date"
                    type="date"
                    value={vehicleFormData.mot_expiry_date}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, mot_expiry_date: e.target.value })}
                    className={vehicleFormData.mot_expiry_date ? 'border-green-500 bg-green-50' : 'border-gray-300'}
                  />
                </div>
                
                <div>
                  <Label htmlFor="tax_status">Tax Status</Label>
                  <Input
                    id="tax_status"
                    value={vehicleFormData.tax_status}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, tax_status: e.target.value })}
                    placeholder="e.g., Taxed, Untaxed, SORN"
                    readOnly
                    className={vehicleFormData.tax_status ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-orange-300'}
                  />
                </div>
                
                <div>
                  <Label htmlFor="co2_emissions">CO2 Emissions (g/km)</Label>
                  <Input
                    id="co2_emissions"
                    value={vehicleFormData.co2_emissions}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, co2_emissions: e.target.value })}
                    placeholder="e.g., 120"
                    readOnly
                    className={vehicleFormData.co2_emissions ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-orange-300'}
                  />
                </div>
                
                <div>
                  <Label htmlFor="vin_number">VIN Number</Label>
                  <Input
                    id="vin_number"
                    value={vehicleFormData.vin_number}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, vin_number: e.target.value.toUpperCase() })}
                    placeholder="17-character VIN"
                    className={vehicleFormData.vin_number ? 'font-mono border-green-500 bg-green-50' : 'font-mono border-gray-300'}
                  />
                </div>
              </div>
            </div>

            {/* Notes & Requirements */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Additional Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="special_requirements">Special Requirements</Label>
                  <textarea
                    id="special_requirements"
                    value={vehicleFormData.special_requirements}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, special_requirements: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md text-sm ${vehicleFormData.special_requirements ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}
                    rows="3"
                    placeholder="Allergies, access instructions, pet hair, smoking, etc."
                  />
                </div>
                
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    value={vehicleFormData.notes}
                    onChange={(e) => setVehicleFormData({ ...vehicleFormData, notes: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md text-sm ${vehicleFormData.notes ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}
                    rows="3"
                    placeholder="General notes about this vehicle..."
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1">
                {editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsVehicleDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Quote Dialog */}
      <Dialog open={isQuickQuoteOpen} onOpenChange={setIsQuickQuoteOpen}>
        <DialogContent className="max-w-[98vw] max-h-[95vh] overflow-y-auto w-[98vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-600" />
              Quick Quote for {customer?.name}
              {quickQuoteContact && ` - ${quickQuoteContact.first_name} ${quickQuoteContact.last_name}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Contact Selection */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="quick_contact">Contact {customer?.customer_type === 'business' && '*'}</Label>
                <Select 
                  value={quickQuoteData.contact_id || '__none__'} 
                  onValueChange={(value) => setQuickQuoteData({ ...quickQuoteData, contact_id: value === '__none__' ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No specific contact</SelectItem>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name}
                        {contact.role && ` (${contact.role})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-lg">Services / Items</h3>
                <Button 
                  type="button"
                  size="sm" 
                  onClick={addQuickQuoteItem}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Part # (SKU)</TableHead>
                    <TableHead className="w-[180px]">Service</TableHead>
                    <TableHead className="w-[200px]">Description</TableHead>
                    <TableHead className="w-[150px]">Vehicle</TableHead>
                    <TableHead className="w-[60px]">Qty</TableHead>
                    <TableHead className="w-[100px]">Unit Price (£)</TableHead>
                    <TableHead className="w-[100px]">Cost Price (£)</TableHead>
                    <TableHead className="w-[80px]">Duration (min)</TableHead>
                    <TableHead className="w-[100px]">Discount (£)</TableHead>
                    <TableHead className="w-[70px]">Tax %</TableHead>
                    <TableHead className="w-[100px]">GP (£)</TableHead>
                    <TableHead className="w-[100px]">Line Total</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quickQuoteData.items.map((item, index) => {
                    const lineSubtotal = (item.quantity * item.unit_price) - item.discount_value;
                    const lineTax = lineSubtotal * (item.tax_rate / 100);
                    const lineTotal = lineSubtotal + lineTax;
                    const lineCost = item.quantity * (item.cost_price || 0);
                    const lineGP = lineSubtotal - lineCost;
                    
                    return (
                      <TableRow key={index}>
                        {/* Part Number / SKU - FIRST COLUMN with auto-lookup */}
                        <TableCell>
                          <Input
                            value={item.sku || ''}
                            onChange={(e) => {
                              const sku = e.target.value;
                              updateQuickQuoteItem(index, 'sku', sku);
                              
                              // Auto-lookup service by SKU
                              const service = services.find(s => s.sku && s.sku.toLowerCase() === sku.toLowerCase());
                              if (service) {
                                const newItems = [...quickQuoteData.items];
                                newItems[index] = {
                                  ...newItems[index],
                                  service_id: service.id,
                                  sku: service.sku,
                                  description: service.description || service.name,
                                  unit_price: service.default_price || 0,
                                  cost_price: service.cost || 0,
                                  duration: service.default_duration_minutes || null
                                };
                                setQuickQuoteData({ ...quickQuoteData, items: newItems });
                              }
                            }}
                            placeholder="SKU"
                            title="Enter part number to auto-fill"
                            className="h-9 font-mono text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={item.service_id || '__custom__'}
                            onValueChange={(value) => updateQuickQuoteItem(index, 'service_id', value === '__custom__' ? null : value)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Select service" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__custom__">Custom item</SelectItem>
                              {services.map((service) => (
                                <SelectItem key={service.id} value={service.id}>
                                  {service.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.description}
                            onChange={(e) => updateQuickQuoteItem(index, 'description', e.target.value)}
                            placeholder="Description"
                            className="h-9"
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={item.vehicle_id || '__none__'}
                            onValueChange={(value) => {
                              if (value === '__add_new__') {
                                setAddVehicleForItemIndex(index);
                                setIsVehicleDialogOpen(true);
                                // Reset vehicle form for new entry
                                resetVehicleForm();
                              } else {
                                updateQuickQuoteItem(index, 'vehicle_id', value === '__none__' ? null : value);
                              }
                            }}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Optional" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">No vehicle</SelectItem>
                              <SelectItem value="__add_new__" className="text-blue-600 font-medium">
                                + Add New Vehicle
                              </SelectItem>
                              {vehicles.map((vehicle) => (
                                <SelectItem key={vehicle.id} value={vehicle.id}>
                                  {vehicle.registration_number} - {vehicle.make} {vehicle.model}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateQuickQuoteItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="h-9"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unit_price}
                            onChange={(e) => updateQuickQuoteItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="h-9"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.cost_price}
                            onChange={(e) => updateQuickQuoteItem(index, 'cost_price', parseFloat(e.target.value) || 0)}
                            className="h-9"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            value={item.duration || ''}
                            onChange={(e) => updateQuickQuoteItem(index, 'duration', parseInt(e.target.value) || null)}
                            placeholder="mins"
                            className="h-9"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.discount_value}
                            onChange={(e) => updateQuickQuoteItem(index, 'discount_value', parseFloat(e.target.value) || 0)}
                            className="h-9"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={item.tax_rate}
                            onChange={(e) => updateQuickQuoteItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                            className="h-9"
                          />
                        </TableCell>
                        <TableCell>
                          <span className={`font-medium ${lineGP >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            £{lineGP.toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">£{lineTotal.toFixed(2)}</span>
                        </TableCell>
                        <TableCell>
                          {quickQuoteData.items.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeQuickQuoteItem(index)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>£{calculateQuoteTotal().subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Tax:</span>
                    <span>£{calculateQuoteTotal().taxTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total:</span>
                    <span className="text-green-600">£{calculateQuoteTotal().total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="notes_public">Customer Notes (visible on quote)</Label>
                <textarea
                  id="notes_public"
                  className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={quickQuoteData.notes_public}
                  onChange={(e) => setQuickQuoteData({ ...quickQuoteData, notes_public: e.target.value })}
                  placeholder="Terms, conditions, or special notes for customer"
                />
              </div>
              <div>
                <Label htmlFor="notes_internal">Internal Notes (not visible to customer)</Label>
                <textarea
                  id="notes_internal"
                  className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={quickQuoteData.notes_internal}
                  onChange={(e) => setQuickQuoteData({ ...quickQuoteData, notes_internal: e.target.value })}
                  placeholder="Internal notes, reminders, or special considerations"
                />
              </div>
            </div>

            {/* Valid Until */}
            <div className="w-64">
              <Label htmlFor="valid_until">Valid Until (Optional)</Label>
              <Input
                id="valid_until"
                type="date"
                value={quickQuoteData.valid_until}
                onChange={(e) => setQuickQuoteData({ ...quickQuoteData, valid_until: e.target.value })}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsQuickQuoteOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveAndSendQuote}
                className="flex-1"
              >
                <Mail className="w-4 h-4 mr-2" />
                Save & Send Quote
              </Button>
              <Button
                type="button"
                onClick={handleBookNow}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Book Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Quick Schedule Dialog */}
      <Dialog open={isQuickScheduleOpen} onOpenChange={setIsQuickScheduleOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule Job</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Complete the scheduling details to create the job</p>
            </div>

            {/* Date & Time Section */}
            <div className="space-y-3">
              <h3 className="font-medium text-sm">Date & Time</h3>
              
              <div className="flex items-center space-x-2 p-3 bg-orange-50 rounded-lg">
                <input
                  type="checkbox"
                  id="flexible_timing_quick"
                  checked={scheduleData.flexible_timing}
                  onChange={(e) => setScheduleData({ 
                    ...scheduleData, 
                    flexible_timing: e.target.checked
                  })}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <Label htmlFor="flexible_timing_quick" className="cursor-pointer font-normal">
                  Customer is flexible on time (Time Not Set - date still required)
                </Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sched_date">Date *</Label>
                  <Input
                    id="sched_date"
                    type="date"
                    value={scheduleData.scheduled_start ? scheduleData.scheduled_start.split('T')[0] : ''}
                    onChange={(e) => {
                      const time = scheduleData.scheduled_start?.split('T')[1] || '09:00';
                      handleStartTimeChange(`${e.target.value}T${time}`);
                    }}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="sched_time">Start Time {!scheduleData.flexible_timing && '*'}</Label>
                  <Input
                    id="sched_time"
                    type="time"
                    value={scheduleData.scheduled_start ? scheduleData.scheduled_start.split('T')[1]?.substring(0, 5) : ''}
                    onChange={(e) => {
                      const date = scheduleData.scheduled_start?.split('T')[0] || '';
                      handleStartTimeChange(`${date}T${e.target.value}`);
                    }}
                    disabled={scheduleData.flexible_timing}
                    required={!scheduleData.flexible_timing}
                    placeholder={scheduleData.flexible_timing ? "Flexible" : ""}
                  />
                </div>

                <div>
                  <Label htmlFor="sched_duration">Duration (minutes) {!scheduleData.flexible_timing && '*'}</Label>
                  <Input
                    id="sched_duration"
                    type="number"
                    min="0"
                    value={scheduleData.manual_duration}
                    onChange={(e) => handleDurationChange(e.target.value)}
                    placeholder="e.g., 60, 90, 120"
                    disabled={scheduleData.flexible_timing}
                    required={!scheduleData.flexible_timing}
                  />
                </div>

                <div>
                  <Label htmlFor="sched_end">End Time</Label>
                  <Input
                    id="sched_end"
                    type="time"
                    value={scheduleData.scheduled_end ? scheduleData.scheduled_end.split('T')[1]?.substring(0, 5) : ''}
                    onChange={(e) => {
                      const date = scheduleData.scheduled_end?.split('T')[0] || scheduleData.scheduled_start?.split('T')[0] || '';
                      setScheduleData({ ...scheduleData, scheduled_end: `${date}T${e.target.value}` });
                    }}
                    disabled={scheduleData.flexible_timing}
                    placeholder={scheduleData.flexible_timing ? "Flexible" : ""}
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated from start + duration</p>
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="space-y-3">
              <h3 className="font-medium text-sm">Location Details</h3>
              <div>
                <Label htmlFor="loc_type">Location Type</Label>
                <Select
                  value={scheduleData.location_type}
                  onValueChange={(value) => {
                    setScheduleData({ ...scheduleData, location_type: value });
                    // Auto-populate customer address if mobile
                    if (value === 'mobile' && customer) {
                      setScheduleData({
                        ...scheduleData,
                        location_type: value,
                        address_1: customer.address_1 || '',
                        address_2: customer.address_2 || '',
                        address_3: customer.address_3 || '',
                        city: customer.city || '',
                        county: customer.county || '',
                        postcode: customer.postcode || ''
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select location type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mobile">Mobile (Go to customer)</SelectItem>
                    <SelectItem value="onsite">On-site (Customer comes to us)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="loc_name">Location Name</Label>
                <Input
                  id="loc_name"
                  value={scheduleData.scheduled_location}
                  onChange={(e) => setScheduleData({ ...scheduleData, scheduled_location: e.target.value })}
                  placeholder="e.g., Customer's driveway, Main workshop"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Address</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setScheduleData({
                      ...scheduleData,
                      address_1: '',
                      address_2: '',
                      address_3: '',
                      city: '',
                      county: '',
                      postcode: ''
                    })}
                    className="text-xs h-7"
                  >
                    Clear Address
                  </Button>
                </div>
                <Input
                  value={scheduleData.address_1}
                  onChange={(e) => setScheduleData({ ...scheduleData, address_1: e.target.value })}
                  placeholder="Address Line 1"
                />
                <Input
                  value={scheduleData.address_2}
                  onChange={(e) => setScheduleData({ ...scheduleData, address_2: e.target.value })}
                  placeholder="Address Line 2"
                />
                <Input
                  value={scheduleData.address_3}
                  onChange={(e) => setScheduleData({ ...scheduleData, address_3: e.target.value })}
                  placeholder="Address Line 3"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={scheduleData.city}
                    onChange={(e) => setScheduleData({ ...scheduleData, city: e.target.value })}
                    placeholder="City/Town"
                  />
                  <Input
                    value={scheduleData.county}
                    onChange={(e) => setScheduleData({ ...scheduleData, county: e.target.value })}
                    placeholder="County"
                  />
                </div>
                <Input
                  value={scheduleData.postcode}
                  onChange={(e) => setScheduleData({ ...scheduleData, postcode: e.target.value.toUpperCase() })}
                  placeholder="Postcode"
                  className="uppercase"
                />
              </div>
            </div>

            {/* Instructions */}
            <div>
              <Label htmlFor="special_inst">Special Instructions</Label>
              <textarea
                id="special_inst"
                className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={scheduleData.special_instructions}
                onChange={(e) => setScheduleData({ ...scheduleData, special_instructions: e.target.value })}
                placeholder="Parking, access codes, etc."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsQuickScheduleOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCompleteScheduling}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Complete & Create Job
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Communication Log Dialog */}
      <Dialog open={isCommLogDialogOpen} onOpenChange={(open) => {
        setIsCommLogDialogOpen(open);
        if (!open) setEditingCommLog(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCommLog ? 'Edit Communication Log' : 'Add Communication Log'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCommLogSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="comm_date">Date & Time *</Label>
                <Input
                  id="comm_date"
                  type="datetime-local"
                  required
                  value={commLogFormData.date}
                  onChange={(e) => setCommLogFormData({ ...commLogFormData, date: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="contact_name">Contact Name *</Label>
                {(contacts.length > 0 || customer.customer_type === 'individual') && !isCustomContactName ? (
                  <Select
                    value={
                      // Convert contact_name back to the correct value for the dropdown
                      commLogFormData.contact_name === customer.name ? '__customer__' :
                      commLogFormData.contact_name
                    }
                    onValueChange={(value) => {
                      if (value === '__custom__') {
                        setIsCustomContactName(true);
                        setCommLogFormData({ ...commLogFormData, contact_name: '' });
                      } else if (value === '__customer__') {
                        // Customer themselves (for individual customers)
                        setCommLogFormData({
                          ...commLogFormData,
                          contact_name: customer.name,
                          contact_phone: customer.phone || '',
                          contact_email: customer.email || ''
                        });
                      } else {
                        // Find the contact and pre-fill details
                        const contact = contacts.find(c => `${c.first_name} ${c.last_name}` === value);
                        if (contact) {
                          setCommLogFormData({
                            ...commLogFormData,
                            contact_name: value,
                            contact_phone: contact.phone || '',
                            contact_email: contact.email || ''
                          });
                        } else {
                          setCommLogFormData({ ...commLogFormData, contact_name: value });
                        }
                      }
                    }}
                  >
                    <SelectTrigger id="contact_name">
                      <SelectValue placeholder="Select contact" />
                    </SelectTrigger>
                    <SelectContent>
                      {customer.customer_type === 'individual' && (
                        <SelectItem value="__customer__">
                          {customer.name} (Customer)
                        </SelectItem>
                      )}
                      {contacts.map((contact) => (
                        <SelectItem key={contact.id} value={`${contact.first_name} ${contact.last_name}`}>
                          {contact.first_name} {contact.last_name} {contact.role && `(${contact.role})`}
                        </SelectItem>
                      ))}
                      <SelectItem value="__custom__">Other (Type Custom Name)</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="space-y-2">
                    <Input
                      id="contact_name"
                      required
                      value={commLogFormData.contact_name}
                      onChange={(e) => setCommLogFormData({ ...commLogFormData, contact_name: e.target.value })}
                      placeholder="Enter contact name"
                    />
                    {(contacts.length > 0 || customer.customer_type === 'individual') && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsCustomContactName(false);
                          setCommLogFormData({ ...commLogFormData, contact_name: '' });
                        }}
                      >
                        Choose from contacts instead
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="communication_method">Communication Method</Label>
                <Select
                  value={commLogFormData.communication_method}
                  onValueChange={(value) => setCommLogFormData({ ...commLogFormData, communication_method: value })}
                >
                  <SelectTrigger id="communication_method">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="in-person">In Person</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2 pt-7">
                <input
                  type="checkbox"
                  id="contact_made"
                  checked={commLogFormData.contact_made}
                  onChange={(e) => setCommLogFormData({ ...commLogFormData, contact_made: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <Label htmlFor="contact_made" className="cursor-pointer font-normal">
                  Contact Made
                </Label>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes *</Label>
              <textarea
                ref={notesInputRef}
                id="notes"
                required
                className="w-full min-h-[120px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                value={commLogFormData.notes}
                onChange={(e) => setCommLogFormData({ ...commLogFormData, notes: e.target.value })}
                placeholder="Enter communication details..."
              />
            </div>

            <div>
              <Label htmlFor="next_callback_date">Next Call Back Date</Label>
              <Input
                id="next_callback_date"
                type="date"
                value={commLogFormData.next_callback_date}
                onChange={(e) => setCommLogFormData({ ...commLogFormData, next_callback_date: e.target.value })}
              />
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium text-sm mb-3">Additional Details (Optional)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={commLogFormData.company_name}
                    onChange={(e) => setCommLogFormData({ ...commLogFormData, company_name: e.target.value })}
                    placeholder="Company name"
                  />
                </div>

                <div>
                  <Label htmlFor="company_phone">Company Phone</Label>
                  <Input
                    id="company_phone"
                    value={commLogFormData.company_phone}
                    onChange={(e) => setCommLogFormData({ ...commLogFormData, company_phone: e.target.value })}
                    placeholder="Company phone"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={commLogFormData.contact_phone}
                    onChange={(e) => setCommLogFormData({ ...commLogFormData, contact_phone: e.target.value })}
                    placeholder="Contact phone"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={commLogFormData.contact_email}
                    onChange={(e) => setCommLogFormData({ ...commLogFormData, contact_email: e.target.value })}
                    placeholder="Contact email"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCommLogDialogOpen(false);
                  setEditingCommLog(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                {editingCommLog ? 'Update Log' : 'Create Log'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CustomerDetailView;
